import { useQuery } from '@tanstack/react-query';
import { useContext, useState, useEffect } from 'react';
// import { useServiceToken } from '@/auth/index';
import { AppConfig } from '@/context/AppConfig';
import { AppRequests } from '@/context/AppRequests';
import { useReportMonitors } from '@/monitoring/useReportMonitors';

// POC for useReportV2, this hook will simplify fetching reports via permalink
// it will request and store both the monitored and non-monitored reports
// in order for this to work you must add `useParams` to the `AppConfig` context in App.tsx

// I believe this could be done without permalink being passed in as a parameter
// will need to investigate further
export const useReportV2 = (permalink: string) => {
  const [permalinkChangeLoading, setPermalinkChangeLoading] = useState(false);
  const { reportIsMonitored } = useReportMonitors();
  // const { data: tokenResult } = useServiceToken('remoulade');
  //  Requests
  const { fetchReportById } = useContext(AppRequests).ReportRequests;
  // const { getSnapshot } = useContext(AppRequests).MonitoringRequests;

  // Grabs reportType from the URL params
  const { useParams } = useContext(AppConfig).routingUtils;
  const { reportType } = useParams() as { reportType: string | null };
  const [noEntitiesFailureCount, setNoEntitiesFailureCount] = useState(0);

  useEffect(() => {
    setPermalinkChangeLoading(true);
  }, [permalink]);

  // State that handles the number of times the 'no_entities' error is thrown
  // Fetch report data will call either fetchReportById or getSnapshot based on the reportIsMonitored
  const results = useQuery({
    retryDelay: 2000,
    // Keep previous data when fetching new data
    // This will prevent the UI from entering an loading state when fetching new data
    keepPreviousData: true,
    enabled: Boolean(reportType),
    queryKey: [{ report: permalink }, { reportIsMonitored }],
    retry: (failureCount: number, error: Error) => {
      // Default retry logic for errors matching 'Report not ready'
      if (error.message.match(/Report not ready/)) {
        return true;
      }

      // Custom retry logic for reportType === 'property' and no_entities === null
      if (
        reportType === 'property' &&
        error.message.indexOf('Continue polling') !== -1
      ) {
        return true;
      }

      return false;
    },
    queryFn: async () => {
      const data =
        // TODO: Currently remoulade has no way of knowing if the report has been
        // upgraded so will always fetch the report from the Core until that is done.
        // reportIsMonitored && tokenResult?.token
        //   ? await getSnapshot(tokenResult.token, permalink)
        //   : await fetchReportById(permalink);
        await fetchReportById(permalink);
      if (
        reportType === 'property' &&
        (data?.meta?.no_entities === null ||
          data?.meta?.no_entities === undefined) &&
        noEntitiesFailureCount < 4
      ) {
        setNoEntitiesFailureCount((prevState) => prevState + 1);

        throw new Error(
          "Continue polling because of 'no_entities' unprocessed..."
        );
      }

      return data;
    },
    onSettled: () => {
      setPermalinkChangeLoading(false);
    },
  });

  const isLoadingNewReport = permalinkChangeLoading || results.isInitialLoading;

  return { ...results, isInitialLoading: isLoadingNewReport };
};
